import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/shared/api/client';
import { sendVerifyEmail } from '@/shared/api/endpoints/auth';
import { isSyntheticPhoneEmail } from '@/shared/lib/emailVerification';

// Lightweight, dismissible banner shown across the SPA while the user has not
// confirmed their email. Honours the backend "soft-gate" model: never blocks a
// route, only nags. Critical actions are gated server-side via
// `RequireEmailVerified` and surface as 403 EMAIL_NOT_VERIFIED elsewhere.

interface ProfileShape {
  email?: string;
  emailVerified?: boolean;
  email_verified?: boolean;
  emailStatus?: string; // ok | bounced | complained
  graceUntil?: string | null;
  grace_until?: string | null;
}

const RESEND_COOLDOWN_SEC = 60;

function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return null;
  const ms = ts - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

export function EmailVerifyBanner() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<ProfileShape | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [status, setStatus] = useState<'idle' | 'sent' | 'error'>('idle');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await apiClient.get('profile/me').json<ProfileShape>();
        if (!cancelled) setProfile(p);
      } catch {
        /* not authenticated yet — banner stays hidden */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = window.setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(id);
  }, [cooldown]);

  if (!profile || dismissed) return null;
  const verified = profile.emailVerified ?? profile.email_verified ?? false;
  if (verified) return null;
  if (!profile.email || isSyntheticPhoneEmail(profile.email)) return null;

  const bounced = (profile.emailStatus ?? '').toLowerCase() === 'bounced';
  const complained = (profile.emailStatus ?? '').toLowerCase() === 'complained';
  const grace = daysUntil(profile.graceUntil ?? profile.grace_until ?? null);
  const expired = grace !== null && grace === 0;

  const tone = bounced || complained
    ? 'border-red-300 bg-red-50 text-red-900'
    : expired
      ? 'border-amber-300 bg-amber-50 text-amber-900'
      : 'border-primary-200 bg-primary-50 text-primary-900';

  const message = bounced
    ? t('verify.bannerBounced', { defaultValue: 'Письмо не доставлено. Проверьте адрес или измените email.' })
    : complained
      ? t('verify.bannerComplained', { defaultValue: 'Письмо помечено как спам. Проверьте папку «Спам» и подтвердите email.' })
      : expired
        ? t('verify.bannerExpired', { defaultValue: 'Срок подтверждения истёк. Часть функций недоступна.' })
        : grace !== null
          ? t('verify.bannerWithGrace', {
              count: grace,
              defaultValue: 'Подтвердите email — осталось {{count}} дн.',
            })
          : t('verify.banner', { defaultValue: 'Подтвердите email для полного доступа.' });

  const handleResend = async () => {
    if (cooldown > 0 || !profile.email) return;
    setCooldown(RESEND_COOLDOWN_SEC);
    try {
      await sendVerifyEmail();
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div
      className={`flex flex-wrap items-center gap-3 border-b px-6 py-2 text-sm ${tone}`}
      role="status"
    >
      <span className="font-medium">{message}</span>
      <button
        type="button"
        onClick={() => void handleResend()}
        disabled={cooldown > 0}
        className="rounded-md border border-current/40 px-3 py-1 text-xs font-semibold transition hover:bg-white/50 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {cooldown > 0
          ? t('verify.resendCooldown', { count: cooldown, defaultValue: 'Повтор через {{count}} с' })
          : t('verify.resend', { defaultValue: 'Отправить письмо ещё раз' })}
      </button>
      {status === 'sent' && (
        <span className="text-xs">{t('verify.resendOk', { defaultValue: 'Письмо отправлено.' })}</span>
      )}
      {status === 'error' && (
        <span className="text-xs">{t('verify.resendErr', { defaultValue: 'Не удалось отправить, попробуйте позже.' })}</span>
      )}
      <Link
        to="/app/verify-email"
        className="text-xs font-semibold underline opacity-80 hover:opacity-100"
      >
        {t('verify.enterCode', { defaultValue: 'Ввести код' })}
      </Link>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="ml-auto text-xs underline opacity-70 hover:opacity-100"
      >
        {t('common.hide', { defaultValue: 'Скрыть' })}
      </button>
    </div>
  );
}
